import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, Divider, Chip } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';

// --- Interfaces for Data Analytics & BI Data ---
interface DataBIOwner { id: string; name: string; }
interface CostLabelPair { cost: string; label: string; }
interface DataBIDetailsData {
    region: string;
    edition: string;
    primaryDataSource: string;
}
interface DataBIApplication { id: string; applicationName: string; }
interface DataBIJobData {
    id: string;
    name: string;
    cloudProvider: string;
    application: DataBIApplication;
    solutionCategoryType: string;
    costLabelPairs: CostLabelPair[];
    status: string;
    details: { dataAnalytics: DataBIDetailsData; };
    solutionOwner: DataBIOwner;
}

const DataBIDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [dataBIService, setDataBIService] = useState<DataBIJobData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) { setIsLoading(false); setError("ID not found."); return; }
            setIsLoading(true);
            try {
                const query = `
                    query GetSolutionDataAnalytic($getDataAnalyticId: ID!) {
                      getDataAnalytic(id: $getDataAnalyticId) {
                        id
                        name
                        cloudProvider
                        application { id applicationName }
                        solutionCategoryType
                        costLabelPairs { cost label }
                        status
                        details { dataAnalytics { region edition primaryDataSource } }
                        solutionOwner { id name }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getDataAnalyticId: id });
                console.log("data",response);
                if (response?.getDataAnalytic) {
                    setDataBIService(response?.getDataAnalytic);
                } else {
                    setError("Data Analytics & BI Service not found.");
                }
            } catch (err: any) {
                setError(err.message || "Failed to fetch details.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const getStatusBackgroundColor = (status: string = "pending"): string => {
        const lowerStatus = status.toLowerCase();
        if (["active", "approved", "success"].includes(lowerStatus)) return "#12B76A10";
        if (["rejected", "failed"].includes(lowerStatus)) return "#F0443810";
        return "#FDB02210";
    };

    if (isLoading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px"><CircularProgress /></Box>;
    if (error) return <Typography color="error" sx={{ p: 2 }}>{error}</Typography>;
    if (!dataBIService) return <Typography sx={{ p: 2 }}>No data available.</Typography>;

    const biDetails = dataBIService?.details?.dataAnalytics;

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { sm: 3 }, pr: { sm: 3 } }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/datasolutions/data-bi" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/datasolutions/data-bi'); }}> Data Analytics & BI </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Data Analytics & BI Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/datasolutions/data-bi')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Data Analytics & BI Details </Typography>
            </Box>
            
            <Box sx={{ mx: 2, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{ p: 2.5, bgcolor: getStatusBackgroundColor(dataBIService.status), borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold' }}>{dataBIService.name}</Typography>
                        {dataBIService.status === "pending" && <IconButton edge="end" onClick={() => navigate(`/datasolutions/data-bi/edit/${dataBIService.id}`)}><img src="/assets/edit-icon.svg" alt="Edit" /></IconButton>}
                    </Box>
                    <Typography variant="body2" color="text.secondary">Owner: {dataBIService.solutionOwner?.name || 'N/A'}</Typography>
                </Box>
                
                <Grid container spacing={3} sx={{ p: 3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}><Typography variant="caption" display="block">Application</Typography><Typography>{dataBIService.application?.applicationName || "N/A"}</Typography></Grid>
                    <Grid item xs={12} sm={4} md={3}><Typography variant="caption" display="block">Cloud Provider</Typography><Typography>{dataBIService.cloudProvider?.toUpperCase()}</Typography></Grid>
                    <Grid item xs={12} sm={4} md={3}><Typography variant="caption" display="block">Service Type</Typography><Typography>{dataBIService.solutionCategoryType}</Typography></Grid>
                    <Grid item xs={12} sm={4} md={3}><Typography variant="caption" display="block">Region</Typography><Typography>{biDetails?.region}</Typography></Grid>
                    <Grid item xs={12} sm={4} md={3}><Typography variant="caption" display="block">Edition</Typography><Typography>{biDetails?.edition}</Typography></Grid>
                </Grid>

                <Box sx={{ p: 2.5, pt: 1.5 }}>
                    <Divider sx={{ mb: 2.5 }} />
                    <Typography component="h3" sx={{ fontFamily: 'Gilroy-Medium', mb: 1 }}>Configuration Details</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4} md={3}><Typography variant="caption" display="block">Primary Data Source</Typography><Typography sx={{wordBreak: 'break-all'}}>{biDetails?.primaryDataSource}</Typography></Grid>
                    </Grid>
                </Box>

                {/* {dataBIService.costLabelPairs && dataBIService.costLabelPairs.length > 0 && (
                    <Box sx={{ p: 2.5, pt: 1.5 }}>
                        <Divider sx={{ mb: 2.5 }} />
                        <Typography component="h3" sx={{ fontFamily: 'Gilroy-Medium', mb: 1 }}>Labels</Typography> 
                        <Box>{dataBIService.costLabelPairs.map((pair, index) => <Chip key={index} label={`${pair.cost}: ${pair.label}`} sx={{ mr: 1, mb: 1 }} />)}</Box>
                    </Box>
                )} */}
            </Box>
        </Container>
    );
};

const DataBIDetailsPage: React.FC = () => {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"datasolutions-data-bi"} />
            <MiniBot/>
            <Box component="main" sx={{ flexGrow: 1, p: 0, bgcolor: '#fafafa' }}>
                <DataBIDetailsPageContent />
            </Box>
        </Box>
    );
};

export default DataBIDetailsPage;